/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, HStack, VStack, Separator } from 'chakra-v3';
import { Text } from '../../components/Text';

const Pipeline: React.FC = () => {
  return (
    <Box
      w="100%"
      maxW="1440px"
      mx="auto"
      pt={{ base: 8, sm: 8, md: '60px', lg: '60px', xl: '60px' }}
      pb={{ base: 8, sm: 8, md: '60px', lg: '60px', xl: '60px' }}
      px={{ base: 4, sm: 6, md: 6 }}
    >
      <VStack
        gap={{ base: 6, sm: 6, md: '24px', lg: '24px', xl: '24px' }}
        alignItems="flex-start"
        w="100%"
        mx="auto"
        pl={{ base: 0, sm: 0, md: 0, lg: '40px', xl: '160px', '2xl': '160px' }}
        pr={{ base: 0, sm: 0, md: 0, lg: '40px', xl: '160px', '2xl': '160px' }}
      >
        {/* Main Title */}
        <Text
          fontFamily="heading"
          fontSize={{ base: '32px', sm: '40px', md: '48px', lg: '72px' }}
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-3px"
          color="#0a313c"
          w="100%"
          whiteSpace="pre-wrap"
          textAlign="left"
        >
          Pipeline
        </Text>

        {/* Intro Text - Two Columns */}
        <Box
          w="100%"
          maxW={{
            base: '100%',
            sm: '100%',
            md: '100%',
            lg: '1080px',
            xl: '1200px',
            '2xl': '1200px',
          }}
        >
          <HStack
            gap={{ base: 6, sm: 6, md: '40px', lg: '40px', xl: '40px' }}
            alignItems="flex-start"
            flexDirection={{ base: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' }}
            w="100%"
          >
            <Text
              fontFamily="body"
              fontSize={{ base: '16px', md: '16px', lg: '16px' }}
              fontWeight="normal"
              lineHeight="1.6"
              color="#1a1a1a"
              flex="1 0 0"
              whiteSpace="pre-wrap"
            >
              Understanding whether an AI system is genuinely useful requires insight from those
              closest to the issues it aims to address. Organisations working at the frontline of
              finance, education, healthcare and legal services carry deep, practical knowledge, yet
              often lack the resources to participate in AI evaluation.
            </Text>
            <Text
              fontFamily="body"
              fontSize={{ base: '16px', md: '16px', lg: '16px' }}
              fontWeight="normal"
              lineHeight="1.6"
              color="#1a1a1a"
              flex="1 0 0"
              whiteSpace="pre-wrap"
            >
              Members of the population experience the consequences of model errors most directly,
              but rarely have structured or supported ways to contribute at scale. And conducting
              rigorous, multilingual evaluation using only manual effort is prohibitively intensive.
              These limitations are structural—and they shape not only what AI learns, but what it
              overlooks.
            </Text>
          </HStack>

          {/* Horizontal Divider */}
          <Separator
            orientation="horizontal"
            w="100%"
            borderColor="rgba(10, 49, 60, 0.2)"
            my={{ base: 4, sm: 4, md: '24px', lg: '24px', xl: '40px', '2xl': '40px' }}
          />

          {/* Subheading */}
          <Box
            w="100%"
            maxW={{ base: '100%', md: '100%', lg: '840px', xl: '840px' }}
            alignSelf="flex-start"
            mb={{ base: 4, sm: 4, md: '24px', lg: '24px', xl: '24px' }}
          >
            <Text
              fontFamily="body"
              fontSize={{ base: '18px', sm: '20px', md: '24px', lg: '24px', xl: '24px' }}
              fontWeight="normal"
              lineHeight="1.4"
              letterSpacing="0.12px"
              color="#0a313c"
              w="100%"
              whiteSpace="pre-wrap"
            >
              Samiksha&apos;s pipeline uses a multi-step process to
            </Text>
          </Box>

          {/* 3-Step Flow Diagram */}
          <Box
            w="100%"
            maxW={{ base: '100%', md: '100%', lg: '1040px', xl: '1040px', '2xl': '1080px' }}
            position="relative"
            alignSelf="flex-start"
          >
            <VStack
              gap={{ base: 10, sm: 10, md: '40px', lg: '40px', xl: '40px' }}
              alignItems="stretch"
              position="relative"
            >
              {/* Vertical Connecting Line */}
              <Separator
                orientation="vertical"
                position="absolute"
                left={{ md: '27px', lg: '27px', xl: '27px' }}
                top={0}
                bottom={0}
                borderColor="rgba(10, 49, 60, 0.2)"
                display={{ base: 'none', sm: 'none', md: 'block', lg: 'block', xl: 'block' }}
              />

              {/* Step 1 */}
              <HStack
                gap={{ base: 3, sm: 4, md: '16px', lg: '16px', xl: '16px' }}
                alignItems="center"
                justifyContent="flex-start"
                position="relative"
                flexDirection="row"
                w="100%"
                flexWrap="nowrap"
              >
                <Box
                  bg="#f3f8fc"
                  border="1px solid"
                  borderColor="rgba(10, 49, 60, 0.2)"
                  borderRadius="4px"
                  w={{ base: '42px', sm: '48px', md: '56px' }}
                  minW={{ base: '42px', sm: '48px', md: '56px' }}
                  py={{ base: '16px', sm: '20px', md: '24px' }}
                  flexShrink={0}
                  alignSelf="stretch"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '18px', sm: '20px', md: '24px' }}
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.1px"
                    color="#1a1a1a"
                  >
                    1
                  </Text>
                </Box>
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="rgba(10, 49, 60, 0.25)"
                  borderRadius="4px"
                  p={{ base: 3, sm: 4, md: 6, lg: 6, xl: 6 }}
                  w={{ base: 'calc(100% - 54px)', sm: 'calc(100% - 64px)', md: 'fit-content' }}
                  maxW={{ base: 'calc(100% - 54px)', sm: 'calc(100% - 64px)', md: 'calc(100% - 72px)' }}
                  flexShrink={{ base: 1, sm: 1, md: 0 }}
                  minW={0}
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '16px', md: '16px', lg: '16px' }}
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.08px"
                    color="#0a313c"
                    whiteSpace="pre-wrap"
                  >
                    elicit rich insights via focused expert consultations
                  </Text>
                </Box>
              </HStack>

              {/* Step 2 */}
              <HStack
                gap={{ base: 3, sm: 4, md: '16px', lg: '16px', xl: '16px' }}
                alignItems="center"
                justifyContent="flex-start"
                position="relative"
                flexDirection="row"
                w="100%"
                flexWrap="nowrap"
              >
                <Box
                  bg="#f9fcf3"
                  border="1px solid"
                  borderColor="rgba(10, 49, 60, 0.2)"
                  borderRadius="4px"
                  w={{ base: '42px', sm: '48px', md: '56px' }}
                  minW={{ base: '42px', sm: '48px', md: '56px' }}
                  py={{ base: '16px', sm: '20px', md: '24px' }}
                  flexShrink={0}
                  alignSelf="stretch"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '18px', sm: '20px', md: '24px' }}
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.1px"
                    color="#1a1a1a"
                  >
                    2
                  </Text>
                </Box>
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="rgba(10, 49, 60, 0.25)"
                  borderRadius="4px"
                  p={{ base: 3, sm: 4, md: 6, lg: 6, xl: 6 }}
                  w={{ base: 'calc(100% - 54px)', sm: 'calc(100% - 64px)', md: 'fit-content' }}
                  maxW={{ base: 'calc(100% - 54px)', sm: 'calc(100% - 64px)', md: 'calc(100% - 72px)' }}
                  flexShrink={{ base: 1, sm: 1, md: 0 }}
                  minW={0}
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '16px', md: '16px', lg: '16px' }}
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.08px"
                    color="#0a313c"
                    whiteSpace="pre-wrap"
                  >
                    expand those insights into a large-scale evaluation via paid data work
                    undertaken by end users and
                  </Text>
                </Box>
              </HStack>

              {/* Step 3 */}
              <HStack
                gap={{ base: 3, sm: 4, md: '16px', lg: '16px', xl: '16px' }}
                alignItems="center"
                justifyContent="flex-start"
                position="relative"
                flexDirection="row"
                w="100%"
                flexWrap="nowrap"
              >
                <Box
                  bg="#f3fcf8"
                  border="1px solid"
                  borderColor="rgba(10, 49, 60, 0.2)"
                  borderRadius="4px"
                  w={{ base: '42px', sm: '48px', md: '56px' }}
                  minW={{ base: '42px', sm: '48px', md: '56px' }}
                  py={{ base: '16px', sm: '20px', md: '24px' }}
                  flexShrink={0}
                  alignSelf="stretch"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '18px', sm: '20px', md: '24px' }}
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.1px"
                    color="#1a1a1a"
                  >
                    3
                  </Text>
                </Box>
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="rgba(10, 49, 60, 0.25)"
                  borderRadius="4px"
                  p={{ base: 3, sm: 4, md: 6, lg: 6, xl: 6 }}
                  w={{ base: 'calc(100% - 54px)', sm: 'calc(100% - 64px)', md: 'fit-content' }}
                  maxW={{ base: 'calc(100% - 54px)', sm: 'calc(100% - 64px)', md: 'calc(100% - 72px)' }}
                  flexShrink={{ base: 1, sm: 1, md: 0 }}
                  minW={0}
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '16px', md: '16px', lg: '16px' }}
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.08px"
                    color="#0a313c"
                    whiteSpace="pre-wrap"
                  >
                    further expand the previous steps into a massive-scale evaluation using a novel
                    LLM-as-a-judge technique
                  </Text>
                </Box>
              </HStack>
            </VStack>
          </Box>

          {/* Horizontal Divider */}
          <Separator
            orientation="horizontal"
            w="100%"
            borderColor="rgba(10, 49, 60, 0.2)"
            my={{ base: 4, sm: 4, md: '24px', lg: '24px', xl: '40px', '2xl': '40px' }}
          />

          {/* Conclusion Text - Two Columns */}
          <HStack
            gap={{ base: 6, sm: 6, md: '40px', lg: '40px', xl: '40px' }}
            alignItems="flex-start"
            flexDirection={{ base: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' }}
            w="100%"
          >
            <Text
              fontFamily="body"
              fontSize={{ base: '16px', md: '16px', lg: '16px' }}
              fontWeight="normal"
              lineHeight="1.6"
              color="#1a1a1a"
              flex="1 0 0"
              whiteSpace="pre-wrap"
            >
              This creates a robust pipeline that combines{' '}
              <Text
                as="span"
                fontFamily="body"
                fontSize={{ base: '16px', md: '16px', lg: '16px' }}
                fontWeight="600"
                color="#0a313c"
              >
                big-picture inputs from experts
              </Text>
              ,{' '}
              <Text
                as="span"
                fontFamily="body"
                fontSize={{ base: '16px', md: '16px', lg: '16px' }}
                fontWeight="600"
                color="#0a313c"
              >
                fine-grained inputs from subsets of the population
              </Text>{' '}
              and{' '}
              <Text
                as="span"
                fontFamily="body"
                fontSize={{ base: '16px', md: '16px', lg: '16px' }}
                fontWeight="600"
                color="#0a313c"
              >
                automated extrapolation with AI techniques
              </Text>{' '}
              to enable high-quality evaluation at scale
            </Text>
            <Text
              fontFamily="body"
              fontSize={{ base: '18px', md: '18px', lg: '18px' }}
              fontWeight="700"
              lineHeight="25px"
              color="#0a313c"
              flex="1 0 0"
              whiteSpace="pre-wrap"
            >
              Together, Samiksha lays the groundwork for AI technologies that are inclusive,
              reliable, and genuinely aligned with the needs of people across India.
            </Text>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default Pipeline;
